import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { explain } from '@/shared/api/problem'
import { fmtDateTimeSec, fmtNumber, fmtRelative } from '@/shared/lib/format'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import {
  getGraph, type GraphAlert, type NarrativeGraph, type NarrativeNode, type SubjectNode,
  type ThreadNode,
} from './api'
import { ExcludedNote, ThreatFilter, useThreatBands } from './ThreatFilter'
import { buildLayout, RING_R, type Layout } from './graph/layout'
import { readPalette, threatInk, threatText } from './graph/palette'
import {
  AlertFeed, Block, Cell, Empty, Grid, NarrativeList, Pill, PlatformFilter, SubjectList, Stat,
  UnreadStat, type Picked,
} from './graph/rails'
import {
  buildPackets, draw, findPoint, hitTest, minimapRect,
  type EncodeMode, type Hover, type Scene, type Selection,
} from './graph/render'
import {
  attachNav, centreOn, ease, LEVEL_STOPS, levelFor, makeViewport, refit, reset, zoomAt, zoomOf,
  type Viewport,
} from './graph/viewport'

/**
 * The narrative console — the War Room's landing board.
 *
 * ## Why a map and not a dashboard
 *
 * A campaign accumulates thousands of threads about tens of subjects. Every list-shaped view of
 * that answers "what is the top of the list", which is a question about the sort order rather
 * than about the country. The thing a room actually needs to see is SHAPE: which subject is
 * carrying the volume, whether it is one narrative or thirty, whether the same sentence is
 * arriving from four hundred accounts, and which channel is feeding it.
 *
 * So the board is a map, and the zoom is the query. Subjects, then narratives, then threads —
 * the same corpus at three honest resolutions, chosen by how far in the reader has gone. Only
 * the level that fits is drawn, which is why a window of four thousand threads costs the same
 * per frame as one of forty.
 *
 * ## Three things this screen refuses to do
 *
 * **It will not render "unread" as "harmless".** A thread nobody has assessed is grey, hollow
 * and counted separately, at every level. The single failure this room exists to prevent is a
 * hostile thread passing unnoticed because a dashboard drew it as a calm dot.
 *
 * **It will not let coordination read as menace.** A coordinated narrative gets its own hue,
 * a dashed outline and the word — never a place on the threat ramp. "The same sentence from
 * four hundred accounts" and "the council scored this 80" are different kinds of fact.
 *
 * **It will not present a sample as a population.** The thread level is capped; every count is
 * computed over the whole window; and when the two differ the footer says so.
 *
 * ## The board polls
 *
 * Thirty seconds, matching the situation board. The radar itself runs on a minute and the
 * approval step is a person reading a paragraph, so a tighter refresh would buy nothing but a
 * flickering screen. A refresh never moves the viewport — a reader mid-gesture keeps their
 * place.
 */
/*
   The window, out to ten years.

   Twenty-four hours is the right DEFAULT — it is the span somebody standing in a war room
   actually works in. It was the wrong CEILING. "Has this been building since the primaries?"
   and "did we ever answer that line?" are questions about months, and a console that cannot be
   widened answers them with an empty board — which reads as "nothing happened" rather than
   "you are looking at the wrong fortnight". The presets cover the operational spans; the field
   takes any number of days up to MAX_WINDOW_DAYS below.
*/
const WINDOW_PRESETS: [number, string][] = [
  [6, '6H'], [24, '24H'], [168, '7D'], [720, '30D'], [2160, '90D'],
]

/**
 * Matches the server clamp in NarrativeGraphService. Ten years — two electoral cycles — so the
 * field reaches back to what the other side was saying last time. Asking for more is silently
 * narrowed there, so the field never offers it.
 */
const MAX_WINDOW_DAYS = 365 * 10

/**
 * Hours, days, months, years — whichever unit makes the span readable at a glance.
 *
 * "2160h" is not a number anybody reads as three months, and "3650d" is not a decade. Days hold
 * out to 90 so the presets keep their own names rather than being rounded into "3m".
 */
function windowLabel(h: number) {
  // Through 24 rather than under it, so the preset labelled 24H is never reported back as "1d".
  // A control and a readout naming the same span differently reads as two different spans.
  if (h <= 24) return `${h}h`
  const d = Math.round(h / 24)
  if (d <= 90) return `${d}d`
  if (d < 365) return `${Math.round(d / 30)}mo`
  const y = d / 365
  return `${y < 10 ? Math.round(y * 10) / 10 : Math.round(y)}y`
}

/**
 * Five presets and a free field.
 *
 * <p>The field is a DRAFT until it is committed, because refetching the graph on every
 * keystroke would fire a query for "3" and another for "30" on the way to "300".</p>
 */
function WindowControl({ hours, onChange }: { hours: number; onChange: (h: number) => void }) {
  const [draft, setDraft] = useState('')
  const custom = !WINDOW_PRESETS.some(([h]) => h === hours)

  const commit = () => {
    const days = Number(draft.trim())
    setDraft('')
    if (!draft.trim() || !Number.isFinite(days) || days < 1) return
    onChange(Math.min(Math.round(days), MAX_WINDOW_DAYS) * 24)
  }

  return (
    <div className="flex items-center gap-px border-r px-1"
         style={{ borderColor: 'var(--graph-panel-line)' }}>
      {WINDOW_PRESETS.map(([h, label]) => (
        <button
          key={h}
          type="button"
          onClick={() => onChange(h)}
          aria-pressed={hours === h}
          className={cn(
            'border px-2.5 py-1.5 font-mono text-[9.5px] tracking-[0.16em] uppercase',
            'transition-colors',
            hours === h
              ? 'border-[color:var(--graph-accent)]/40 bg-[color:var(--graph-accent)]/[0.08]'
              : 'border-transparent hover:opacity-80',
          )}
          style={{ color: hours === h ? 'var(--graph-accent)' : 'var(--graph-ink-faint)' }}
        >
          {label}
        </button>
      ))}
      <label
        title={'Any window, in days, up to ten years — type a number and press Enter'}
        className={cn(
          'ml-0.5 flex items-center border py-1 pl-1.5 pr-1 transition-colors',
          custom
            ? 'border-[color:var(--graph-accent)]/40 bg-[color:var(--graph-accent)]/[0.08]'
            : 'border-transparent hover:opacity-80',
        )}
        style={{ color: custom ? 'var(--graph-accent)' : 'var(--graph-ink-faint)' }}
      >
        <span className="sr-only">Custom window, in days</span>
        <input
          type="text"
          inputMode="numeric"
          value={draft !== '' ? draft : custom ? String(Math.round(hours / 24)) : ''}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') commit() }}
          onBlur={commit}
          placeholder="··"
          className="w-11 bg-transparent text-right font-mono text-[9.5px] tabular-nums
                     outline-none placeholder:opacity-40"
          style={{ color: 'inherit' }}
        />
        <span className="pl-1 font-mono text-[9.5px] tracking-[0.16em] uppercase">d</span>
      </label>
    </div>
  )
}

export function IntelligencePage() {
  const navigate = useNavigate()
  const [graph, setGraph] = useState<NarrativeGraph | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Twenty-four hours: the span somebody standing in a war room actually works in.
  //
  // It is only a safe DEFAULT because of the overlay below. A quiet day and an empty corpus draw
  // the identical blank board, so landing on a narrow window used to leave the reader unable to
  // tell "nothing happened" from "you are looking at the wrong fortnight". The board now says
  // which of the two it is and offers the next span up, so the default can stay operational.
  const [windowHours, setWindowHours] = useState(24)
  const [hidden, setHidden] = useState<Set<string>>(() => new Set())
  const [selected, setSelected] = useState<Selection | null>(null)
  const [hovered, setHovered] = useState<Hover | null>(null)
  const [paused, setPaused] = useState(false)
  const [showLabels, setShowLabels] = useState(true)
  const [showFlow, setShowFlow] = useState(true)
  const [query, setQuery] = useState('')
  const [zoomLabel, setZoomLabel] = useState('×1.0')
  const [encode, setEncode] = useState<EncodeMode>('THREAT')
  const [immersive, setImmersive] = useState(false)
  const [log, setLog] = useState<LogLine[]>([])
  const [clock, setClock] = useState('00:00:00')
  const [band, setBand] = useState('')

  // The next span up, for the empty-board offer below. Ends at the server's own ceiling so the
  // button never asks for a window that would be silently clamped back.
  const wider = useMemo(
    () => [...WINDOW_PRESETS.map(([h]) => h), MAX_WINDOW_DAYS * 24]
      .find((h) => h > windowHours) ?? null,
    [windowHours])
  const threatBands = useThreatBands()

  const stageRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const viewportRef = useRef<Viewport>(makeViewport(0.6, 22))
  const sceneRef = useRef<Scene | null>(null)
  const layoutRef = useRef<Layout | null>(null)

  // ── data ───────────────────────────────────────────────────────────────────

  // True once the first payload has landed and the board (and its canvas) is mounted.
  const ready = graph !== null

  const load = useCallback(async () => {
    try {
      setError(null)
      setGraph(await getGraph({ windowHours, band: band || undefined }))
    } catch (err) {
      setError(explain(err))
    }
  }, [windowHours, band])

  useEffect(() => {
    void load()
    const timer = setInterval(() => void load(), 30_000)
    return () => clearInterval(timer)
  }, [load])

  // ── search ─────────────────────────────────────────────────────────────────

  /**
   * Matching thread ids, or null when there is no query.
   *
   * Null and "the empty set" are deliberately different: no query means draw everything at full
   * strength, whereas a query matching nothing must dim the whole board — that is the honest
   * rendering of "your search found nothing", and it is not the same picture as no search.
   */
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q || !graph) return null
    const out = new Set<string>()
    for (const t of graph.threads) {
      const hay = `${t.title ?? ''} ${t.authorHandle ?? ''} ${t.subjectName ?? ''}`.toLowerCase()
      if (hay.includes(q)) out.add(t.id)
    }
    return out
  }, [query, graph])

  // ── the canvas ─────────────────────────────────────────────────────────────

  /**
   * Everything the frame loop reads, in refs.
   *
   * ## Why the loop is not rebuilt when the data changes
   *
   * The board polls every thirty seconds. The first version listed `graph` among the effect's
   * dependencies, so each poll tore the animation down and built a new one: the loop stopped,
   * the layout was recomputed, the particles were re-seeded from scratch, and the next frame
   * started from a blank canvas. That is a visible flash twice a minute, on a screen people
   * watch continuously — and it is entirely self-inflicted, because none of the canvas plumbing
   * actually needed to change.
   *
   * So the loop is set up ONCE and reads mutable refs. New data is swapped in underneath it by
   * the effect below, between frames, and the reader sees the numbers change without the
   * picture ever going away.
   */
  const layoutRef2 = useRef<Layout | null>(null)
  const packetsRef = useRef<ReturnType<typeof buildPackets>>([])
  const paletteRef = useRef<ReturnType<typeof readPalette> | null>(null)
  const modeRef = useRef({ paused, showLabels, showFlow, encode, threshold: 7 })
  modeRef.current = { paused, showLabels, showFlow, encode, threshold: graph?.deployThreshold ?? 7 }
  const hiddenRef = useRef(hidden)
  hiddenRef.current = hidden

  /**
   * Rebuild the layout when the DATA changes, preserving the animation's phase.
   *
   * Particle positions are carried across by (subject, platform) so a refresh does not visibly
   * re-seed the flow. A packet whose pair has gone starts fresh, which is correct — it is a new
   * connection, and it should look like one.
   */
  useEffect(() => {
    if (!graph) return
    const layout = buildLayout(graph, { hiddenPlatforms: hidden })
    const phaseBySubjectPlatform = new Map<string, number[]>()
    for (const p of packetsRef.current) {
      const key = `${p.subjectId}|${p.platformCode}`
      const list = phaseBySubjectPlatform.get(key)
      if (list) list.push(p.t)
      else phaseBySubjectPlatform.set(key, [p.t])
    }
    const packets = buildPackets(layout, hidden)
    const taken = new Map<string, number>()
    for (const p of packets) {
      const key = `${p.subjectId}|${p.platformCode}`
      const list = phaseBySubjectPlatform.get(key)
      const i = taken.get(key) ?? 0
      if (list && list[i] !== undefined) p.t = list[i]!
      taken.set(key, i + 1)
    }
    layoutRef2.current = layout
    layoutRef.current = layout
    packetsRef.current = packets
  }, [graph, hidden])

  useEffect(() => {
    const canvas = canvasRef.current
    const stage = stageRef.current
    if (!canvas || !stage) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const viewport = viewportRef.current
    paletteRef.current = readPalette(stage)
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    let width = 0
    let height = 0
    let dpr = 1

    let firstFit = viewport.fitK === 1 && viewport.tx === 0 && viewport.ty === 0
    const resize = () => {
      const rect = stage.getBoundingClientRect()
      width = Math.max(320, rect.width)
      height = Math.max(320, rect.height)
      dpr = Math.min(window.devicePixelRatio || 1, 2)
      canvas.width = width * dpr
      canvas.height = height * dpr
      canvas.style.width = `${width}px`
      canvas.style.height = `${height}px`
      refit(viewport, width, height, RING_R)
      if (firstFit) {
        reset(viewport, width, height)
        viewport.k = viewport.tk
        viewport.x = viewport.tx
        viewport.y = viewport.ty
        firstFit = false
      }
    }
    resize()

    const observer = new ResizeObserver(resize)
    observer.observe(stage)
    const nav = attachNav(canvas, viewport)

    let raf = 0
    let last = performance.now()
    let drawFailed = false
    const frame = (now: number) => {
      // Re-armed FIRST, before any work. With the request at the end, a single exception in
      // draw() skipped it and the board froze permanently on a still image with no visible
      // error — the worst failure mode for a screen somebody is presenting from.
      raf = requestAnimationFrame(frame)
      const dt = Math.min(0.05, (now - last) / 1000)
      last = now
      const layout = layoutRef2.current
      const palette = paletteRef.current
      if (layout && palette) {
        const mode = modeRef.current
        const scene: Scene = {
          layout, palette, viewport, width, height, dpr, now,
          paused: mode.paused || reduced,
          showLabels: mode.showLabels,
          showFlow: mode.showFlow && !reduced,
          hidden: hiddenRef.current,
          hovered: hoverRef.current,
          selected: selectedRef.current,
          matches: matchesRef.current,
          encode: mode.encode,
          reachMax: reachMaxRef.current,
          threshold: mode.threshold,
        }
        sceneRef.current = scene
        ease(viewport)
        try {
          draw(ctx, scene, packetsRef.current, dt)
        } catch (err) {
          // One bad frame must not end the animation. The re-arm above already happened, so the
          // loop survives; this only stops a single frame's worth of drawing. Reported once,
          // because a throw that repeats every frame would otherwise fill the console at 60Hz
          // and bury whatever caused it.
          if (!drawFailed) {
            drawFailed = true
            console.error('[war-room console] frame failed; animation continues', err)
          }
        }
      }
    }
    raf = requestAnimationFrame(frame)

    return () => {
      cancelAnimationFrame(raf)
      observer.disconnect()
      nav.detach()
    }
    // Two dependencies, both structural — neither is data.
    //
    // `immersive` because entering full screen moves the board into a portal, and React unmounts
    // and remounts the subtree when its parent changes that way, so `canvasRef` points at a NEW
    // element and the loop must re-bind.
    //
    // `ready` because until the first payload arrives the page renders a loading block and there
    // is no canvas in the DOM at all — the effect returns early, and with no dependency that
    // changes on arrival it would never run again. That is exactly the bug that showed up as a
    // permanently blank stage.
    //
    // Data, modes and pointer state all arrive through refs, so a poll never causes a teardown.
  }, [immersive, ready])

  // Refs the frame loop reads without being rebuilt.
  const hoverRef = useRef<Hover | null>(null)
  const selectedRef = useRef<Selection | null>(null)
  const matchesRef = useRef<Set<string> | null>(null)
  const reachMaxRef = useRef(1)
  const immersiveRef = useRef(false)
  immersiveRef.current = immersive
  hoverRef.current = hovered
  selectedRef.current = selected
  matchesRef.current = matches
  reachMaxRef.current = useMemo(
    () => Math.max(1, ...(graph?.threads ?? []).map((t) => t.engagements ?? 0)),
    [graph],
  )

  // Keep the zoom readout in step with the eased scale without re-rendering the page on every
  // frame — a 100ms sample is well under the eye's threshold for a numeric readout.
  useEffect(() => {
    const timer = setInterval(() => {
      setZoomLabel(`×${zoomOf(viewportRef.current).toFixed(1)}`)
    }, 100)
    return () => clearInterval(timer)
  }, [])

  /**
   * The session clock.
   *
   * It is not decoration. This board is read during an incident, often by people standing
   * behind whoever is driving it, and "how long have we been watching this" is a question that
   * gets asked out loud. A wall clock cannot answer it; an elapsed timer can.
   */
  useEffect(() => {
    const started = Date.now()
    const timer = setInterval(() => {
      const s = Math.floor((Date.now() - started) / 1000)
      setClock([s / 3600, (s % 3600) / 60, s % 60]
        .map((n) => String(Math.floor(n)).padStart(2, '0')).join(':'))
    }, 1000)
    return () => clearInterval(timer)
  }, [])

  /**
   * The signal log.
   *
   * Written from what each refresh actually brought back, by diffing against the previous
   * payload. Its job is to make the room's activity legible when nobody is looking at the map —
   * the difference between "quiet" and "the poller died" is the whole point, and a static board
   * cannot show it.
   */
  const seenRef = useRef<{ threads: Set<string>, alerts: Set<string> } | null>(null)
  useEffect(() => {
    if (!graph) return
    const now = new Date()
    const stamp = now.toTimeString().slice(0, 8)
    const previous = seenRef.current
    const threads = new Set(graph.threads.map((t) => t.id))
    const alerts = new Set(graph.alerts.map((a) => a.id))
    const lines: LogLine[] = []

    if (!previous) {
      lines.push({ id: `boot-${now.getTime()}`, at: stamp,
        text: `Window opened · ${fmtNumber(graph.threadTotal)} threads · ${graph.totals.subjects} subjects` })
    } else {
      const newThreads = [...threads].filter((id) => !previous.threads.has(id)).length
      const newAlerts = graph.alerts.filter((a) => !previous.alerts.has(a.id))
      const closed = [...previous.alerts].filter((id) => !alerts.has(id)).length
      if (newThreads) {
        lines.push({ id: `t-${now.getTime()}`, at: stamp,
          text: `${newThreads} new thread${newThreads === 1 ? '' : 's'} detected` })
      }
      for (const a of newAlerts.slice(0, 4)) {
        lines.push({ id: `a-${a.id}-${now.getTime()}`, at: stamp,
          text: `${a.severity} · ${a.title}`, severity: a.severity })
      }
      if (closed) {
        // An alert that stops firing is as worth logging as one that starts. Without this the
        // log only ever gets worse, and a board that only reports bad news stops being read.
        lines.push({ id: `c-${now.getTime()}`, at: stamp,
          text: `${closed} alert${closed === 1 ? '' : 's'} no longer firing` })
      }
      if (!lines.length) {
        lines.push({ id: `q-${now.getTime()}`, at: stamp, text: 'Refreshed · no change' })
      }
    }
    seenRef.current = { threads, alerts }
    setLog((prev) => [...lines.reverse(), ...prev].slice(0, 60))
  }, [graph])

  // ── immersive ──────────────────────────────────────────────────────────────

  /**
   * Full-screen the console.
   *
   * ## An overlay, and deliberately NOT the browser's fullscreen API
   *
   * What the room wants is the app's own chrome gone — sidebar, top bar, status bar — because in
   * a briefing those are somebody else's navigation and not part of what is being shown. An
   * overlay does exactly that, every time.
   *
   * Requesting native fullscreen as well was the first attempt and it was worse than useless:
   * the two states then have to be kept in step, and they cannot be. The browser can refuse the
   * request, grant it and revoke it, or exit it for reasons of its own — and each of those
   * fires a change event that drags the overlay along with it. In testing the console opened
   * and closed again within the same frame. The reader who genuinely wants the browser
   * fullscreened still has F11, and it composes with this cleanly because the two no longer
   * know about each other.
   */
  const toggleImmersive = useCallback(() => setImmersive((was) => !was), [])

  // The stage changes size when the overlay opens or closes. The ResizeObserver re-fits the
  // viewport, and because the world has fixed coordinates the reader keeps their place.

  // ── interaction ────────────────────────────────────────────────────────────

  const flyTo = useCallback((sel: Selection, k?: number) => {
    const layout = layoutRef.current
    const scene = sceneRef.current
    if (!layout || !scene) return
    const point = findPoint(layout, sel)
    if (!point) return
    setSelected(sel)
    centreOn(viewportRef.current, point.x, point.y, scene.width, scene.height,
      k ?? (sel.kind === 'thread' ? 9 : sel.kind === 'narrative' ? 5.5 : 2.4))
  }, [])

  const onMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const scene = sceneRef.current
    if (!scene) return
    const rect = e.currentTarget.getBoundingClientRect()
    setHovered(hitTest(scene, e.clientX - rect.left, e.clientY - rect.top))
  }

  const onClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const scene = sceneRef.current
    if (!scene) return
    const rect = e.currentTarget.getBoundingClientRect()
    const sx = e.clientX - rect.left
    const sy = e.clientY - rect.top

    // The minimap is a control, not decoration: clicking it jumps the viewport there.
    const mini = minimapRect()
    if (mini && sx >= mini.mx && sx <= mini.mx + mini.mw
      && sy >= mini.my && sy <= mini.my + mini.mh) {
      centreOn(viewportRef.current, (sx - mini.ox) / mini.s, (sy - mini.oy) / mini.s,
        scene.width, scene.height)
      return
    }

    const hit = hitTest(scene, sx, sy)
    if (!hit) {
      setSelected(null)
      return
    }
    if (hit.kind === 'platform') {
      toggle(hit.id)
      return
    }
    setSelected({ kind: hit.kind, id: hit.id })
  }

  const onDoubleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const scene = sceneRef.current
    if (!scene) return
    const rect = e.currentTarget.getBoundingClientRect()
    const hit = hitTest(scene, e.clientX - rect.left, e.clientY - rect.top)
    if (!hit || hit.kind === 'platform') return
    // Double-click descends a level, which is the gesture a map trains people to expect.
    if (hit.kind === 'thread') {
      navigate(`/war-room/threads/${hit.id}`)
      return
    }
    flyTo({ kind: hit.kind, id: hit.id },
      hit.kind === 'subject' ? LEVEL_STOPS.narrative + 0.6 : LEVEL_STOPS.thread + 1.2)
  }

  const toggle = useCallback((code: string) => {
    setHidden((prev) => {
      const next = new Set(prev)
      if (next.has(code)) next.delete(code)
      else next.add(code)
      return next
    })
  }, [])

  const solo = useCallback((code: string) => {
    setHidden((prev) => {
      const all = graph?.platforms.map((p) => p.code) ?? []
      const isSolo = prev.size === all.length - 1 && !prev.has(code)
      return isSolo ? new Set() : new Set(all.filter((c) => c !== code))
    })
  }, [graph])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Immersive first: the reader's most recent, most reversible action is the one Escape
        // should undo.
        if (immersiveRef.current) setImmersive(false)
        else setSelected(null)
      }
      if (e.key === '0') {
        const rect = stageRef.current?.getBoundingClientRect()
        reset(viewportRef.current, rect?.width ?? 800, rect?.height ?? 600)
      }
      if (e.key === 'f' || e.key === 'F') {
        const el = document.activeElement
        if (!(el instanceof HTMLInputElement) && !(el instanceof HTMLTextAreaElement)) {
          toggleImmersive()
        }
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [toggleImmersive])

  const picked: Picked | null = useMemo(() => {
    if (!graph || !selected) return null
    if (selected.kind === 'subject') {
      const node = graph.subjects.find((s) => s.id === selected.id)
      return node ? { kind: 'subject', node } : null
    }
    if (selected.kind === 'narrative') {
      const node = graph.narratives.find((n) => n.id === selected.id)
      return node ? { kind: 'narrative', node } : null
    }
    const node = graph.threads.find((t) => t.id === selected.id)
    return node ? { kind: 'thread', node } : null
  }, [graph, selected])

  const onAlert = useCallback((alert: GraphAlert) => {
    if (alert.threadId) flyTo({ kind: 'thread', id: alert.threadId })
    else if (alert.narrativeId) flyTo({ kind: 'narrative', id: alert.narrativeId })
    else if (alert.subjectId) flyTo({ kind: 'subject', id: alert.subjectId })
  }, [flyTo])

  // ── render ─────────────────────────────────────────────────────────────────

  if (error && !graph) return <ErrorStrip message={error} />
  if (!graph) return <LoadingBlock label="Reading the room" />

  const level = levelFor(zoomOf(viewportRef.current))
  const hoveredNode = hovered ? describeHover(graph, hovered) : null

  const board = (
    <div className={cn(
      'axis-console relative flex flex-col overflow-hidden',
      immersive
        ? 'h-dvh w-screen'
        : '-mx-3 -mb-3 h-[calc(100dvh-9.5rem)] min-h-[560px] border',
    )}
         style={{ borderColor: 'var(--graph-panel-line)' }}>
      {/* A very faint scanline wash over the whole console. It is the one purely atmospheric
          thing here, and it is set at an opacity where it reads as a surface texture rather
          than as an effect — enough to make the board feel like an instrument, not enough to
          cost a point of contrast on any label. Removed entirely for reduced motion readers,
          since it is the kind of high-frequency pattern that provokes discomfort. */}
      <div aria-hidden className="pointer-events-none absolute inset-0 z-50 motion-reduce:hidden"
           style={{
             backgroundImage:
               'repeating-linear-gradient(0deg, rgba(255,255,255,.014) 0 1px, transparent 1px 3px)',
             mixBlendMode: 'overlay',
           }} />

      {/* ── top ── */}
      <header className="flex shrink-0 items-stretch border-b"
              style={{ borderColor: 'var(--graph-panel-line)' }}>
        <div className="flex items-center gap-3 border-r px-4 py-2.5"
             style={{ borderColor: 'var(--graph-panel-line)' }}>
          <Seal />
          <span className="flex flex-col justify-center">
            <b className="text-[15px] leading-tight font-normal"
               style={{ color: 'var(--graph-ink)' }}>Narrative Console</b>
            <span className="axis-console-eyebrow mt-px"
                  style={{ color: 'var(--graph-ink-faint)' }}>Social War Room</span>
          </span>
        </div>

        {/* What colour and size are encoding. Three genuinely different questions — see the
            note on EncodeMode — so the control is a mode, not a toggle. */}
        <div className="flex items-center gap-px border-r px-1"
             style={{ borderColor: 'var(--graph-panel-line)' }}>
          {([
            ['THREAT', 'Threat'],
            ['COORDINATION', 'Coordination'],
            ['REACH', 'Reach'],
          ] as [EncodeMode, string][]).map(([mode, label]) => (
            <button
              key={mode}
              type="button"
              aria-pressed={encode === mode}
              onClick={() => setEncode(mode)}
              title={ENCODE_HINT[mode]}
              className={cn(
                'border px-3 py-1.5 font-mono text-[9.5px] tracking-[0.16em] uppercase',
                'transition-colors',
                encode === mode
                  ? 'border-[color:var(--graph-accent)]/40 bg-[color:var(--graph-accent)]/[0.08]'
                  : 'border-transparent hover:opacity-80',
              )}
              style={{ color: encode === mode ? 'var(--graph-accent)' : 'var(--graph-ink-faint)' }}
            >
              {label}
            </button>
          ))}
        </div>

        <WindowControl hours={windowHours} onChange={setWindowHours} />

        <label className="flex flex-1 items-center gap-2 px-3">
          <span className="sr-only">Search threads by title, author or subject</span>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search the window — title, handle, subject"
            className="w-full max-w-md bg-transparent py-2 text-[11.5px] outline-none
                       placeholder:opacity-45"
            style={{ color: 'var(--graph-ink)' }}
          />
          {matches ? (
            <span className="font-mono text-[9.5px] tabular-nums"
                  style={{ color: 'var(--graph-accent)' }}>
              {matches.size} / {graph.threads.length}
            </span>
          ) : null}
        </label>

        <div className="flex items-center gap-6 border-l px-5"
             style={{ borderColor: 'var(--graph-panel-line)' }}>
          <Stat label="Threads" value={fmtNumber(graph.totals.threads)} />
          {/* "Above threshold" is a reading about a server setting nobody outside this
              screen has seen. The number is the same; the tile now answers "above what?"
              where the reader is, instead of hiding it in a hover. */}
          <Stat label="Needs a response" value={fmtNumber(graph.totals.aboveThreshold)}
                hot={graph.totals.aboveThreshold > 0}
                sub={`threat ${graph.deployThreshold ?? 7}+ of 10`} />
          {/* Against the WINDOW, never the drawn sample: with a filter active the sample can be
              zero, and "343/0" is not a fact about anything. */}
          <UnreadStat unscored={graph.totals.unscored} of={graph.totals.threads} />
          <Stat label="Coordinated" value={fmtNumber(graph.totals.coordinated)}
                sub="accounts posting in lockstep"
                hot={graph.totals.coordinated > 0} />
          <Stat label="Awaiting a human" value={fmtNumber(graph.totals.draftsAwaiting)} />
          <Stat label="Session" value={clock} />
        </div>

        <div className="flex items-center border-l px-1.5"
             style={{ borderColor: 'var(--graph-panel-line)' }}>
          <button
            type="button"
            onClick={toggleImmersive}
            aria-pressed={immersive}
            title={immersive
              ? 'Leave full screen (Esc or F)'
              : 'Full screen — hides the app chrome for a briefing (F)'}
            className="axis-keep-narrow flex size-9 items-center justify-center border
                       transition-colors hover:opacity-80"
            style={{
              borderColor: immersive ? 'var(--graph-accent)' : 'var(--graph-panel-line)',
              color: immersive ? 'var(--graph-accent)' : 'var(--graph-ink-faint)',
            }}
          >
            <ExpandIcon collapsed={immersive} />
            <span className="sr-only">{immersive ? 'Leave full screen' : 'Full screen'}</span>
          </button>
        </div>
      </header>

      {/* ── the threat filter ──
          Its own strip under the header rather than floating over the canvas: it is chrome, and
          a control laid over the map collides with the caption at narrow widths and steals
          drags meant for the graph. */}
      <div className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-1 border-b px-4 py-2"
           style={{ borderColor: 'var(--graph-panel-line)' }}>
        <ThreatFilter bands={threatBands} value={band} onChange={setBand} tone="console" />
        <ExcludedNote band={band} matched={graph.threadTotal}
                      windowTotal={graph.totals.threads} unscored={graph.totals.unscored}
                      tone="console" />
      </div>

      {/* ── stage ── */}
      <div className="grid min-h-0 flex-1"
           style={{ gridTemplateColumns: '300px 1fr 340px', gap: 1,
                    background: 'var(--graph-panel-line)' }}>

        <aside className="axis-console-rail min-h-0 overflow-y-auto">
          <Block title="Channels" note={`${graph.platforms.length}`}
                 storageKey="channels" maxBodyHeight={272}>
            <PlatformFilter
              platforms={graph.platforms}
              hidden={hidden}
              onToggle={toggle}
              onSolo={solo}
              onHover={(code) => setHovered(
                code ? { kind: 'platform', id: code, sx: 0, sy: 0 } : null,
              )}
            />
            {hidden.size ? (
              <button type="button" onClick={() => setHidden(new Set())}
                      className="mt-2 font-mono text-[9px] tracking-[0.14em] uppercase"
                      style={{ color: 'var(--graph-accent)' }}>
                Clear filter ({hidden.size} hidden)
              </button>
            ) : null}
          </Block>

          <Block title="Subjects" note={`${graph.totals.subjects}`}
                 storageKey="subjects" maxBodyHeight={200}>
            <SubjectList
              subjects={graph.subjects}
              selectedId={selected?.kind === 'subject' ? selected.id : null}
              onPick={(id) => flyTo({ kind: 'subject', id })}
              threshold={graph.deployThreshold}
            />
          </Block>

          <Block title="Narratives" note={`${graph.totals.narratives}`}
                 storageKey="narratives" maxBodyHeight={240}>
            <NarrativeList
              narratives={graph.narratives}
              selectedId={selected?.kind === 'narrative' ? selected.id : null}
              onPick={(id) => flyTo({ kind: 'narrative', id })}
              threshold={graph.deployThreshold}
            />
          </Block>

          <Block title="Signal log" note={paused ? 'held' : 'live'} className="border-b-0"
                 storageKey="signal-log" maxBodyHeight={260}>
            <SignalLog lines={log} />
          </Block>
        </aside>

        {/* ── the map ── */}
        <div ref={stageRef} className="relative min-h-0 min-w-0 overflow-hidden">
          <canvas
            ref={canvasRef}
            className="block size-full"
            onMouseMove={onMove}
            onMouseLeave={() => setHovered(null)}
            onClick={onClick}
            onDoubleClick={onDoubleClick}
          />

          <div className="pointer-events-none absolute top-4 left-5 max-w-sm">
            <h1 className="text-[24px] leading-tight font-light"
                style={{ color: 'var(--graph-ink)' }}>
              What is being said,{' '}
              <em className="not-italic" style={{ color: 'var(--graph-accent)' }}>
                and who is saying it together.
              </em>
            </h1>
            <p className="mt-2 font-mono text-[9px] tracking-[0.17em] uppercase"
               style={{ color: 'var(--graph-ink-faint)' }}>
              {level === 'SUBJECT' ? 'Subjects' : level === 'NARRATIVE' ? 'Narratives' : 'Threads'}
              {' · '}scroll to zoom · drag to pan · double-click to descend
            </p>
          </div>

          {/* An empty room and a window pointed at the wrong fortnight draw the SAME picture —
              a blank board — and a reader takes both to mean "nothing happened". Only one of
              those is a fact about the room, so say which this is and offer the next span up.
              Deliberately no claim that older threads DO exist: this payload cannot see past
              its own window, and inventing that reassurance would be a guess. */}
          {graph.totals.threads === 0 ? (
            <div className="absolute inset-0 z-10 flex items-center justify-center px-6">
              <div className="pointer-events-auto max-w-sm border px-5 py-4 text-center"
                   style={{ borderColor: 'var(--graph-panel-line)',
                            background: 'var(--graph-stage)' }}>
                <p className="font-mono text-[9px] tracking-[0.17em] uppercase"
                   style={{ color: 'var(--graph-ink-faint)' }}>
                  Nothing in the last {windowLabel(graph.windowHours)}
                </p>
                <p className="mt-2 text-[12.5px] leading-relaxed"
                   style={{ color: 'var(--graph-ink)' }}>
                  The board is empty because the window is, which is not the same as the room
                  being quiet. Widen it before reading anything into this.
                </p>
                {wider ? (
                  <button
                    type="button"
                    onClick={() => setWindowHours(wider)}
                    className="mt-3 border px-3 py-1.5 font-mono text-[9.5px] tracking-[0.16em]
                               uppercase transition-colors hover:opacity-80"
                    style={{ borderColor: 'color-mix(in oklab, var(--graph-accent) 40%, transparent)',
                             color: 'var(--graph-accent)' }}
                  >
                    Widen to {windowLabel(wider)}
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}

          <div className="absolute top-4 right-4 z-10 flex gap-px border"
               style={{ borderColor: 'var(--graph-panel-line)',
                        background: 'var(--graph-panel-line)' }}>
            {[
              { label: 'Labels', on: showLabels, set: setShowLabels },
              { label: 'Flow', on: showFlow, set: setShowFlow },
              { label: 'Pause', on: paused, set: setPaused },
            ].map((t) => (
              <button
                key={t.label}
                type="button"
                aria-pressed={t.on}
                onClick={() => t.set((v) => !v)}
                className="px-3 py-1.5 font-mono text-[9px] tracking-[0.14em] uppercase
                           transition-colors"
                style={{
                  color: t.on ? 'var(--graph-accent)' : 'var(--graph-ink-faint)',
                  background: t.on
                    ? 'color-mix(in oklab, var(--graph-accent) 9%, var(--graph-stage))'
                    : 'var(--graph-stage)',
                }}
              >
                {t.label}
              </button>
            ))}
            {/* Re-centres on whatever is selected. After panning around at thread zoom the
                selection is routinely off-screen, and hunting for it by dragging is the single
                most tedious thing about a map like this. */}
            <button
              type="button"
              disabled={!selected}
              onClick={() => selected && flyTo(selected)}
              className="px-3 py-1.5 font-mono text-[9px] tracking-[0.14em] uppercase
                         transition-colors disabled:opacity-40"
              style={{
                color: 'var(--graph-ink-faint)',
                background: 'var(--graph-stage)',
              }}
            >
              Focus
            </button>
          </div>

          <div className="absolute right-4 bottom-4 z-10 flex flex-col gap-px border"
               style={{ borderColor: 'var(--graph-panel-line)',
                        background: 'var(--graph-panel-line)' }}>
            <ZoomButton onClick={() => zoomAtCentre(viewportRef.current, stageRef.current, 1.45)}>
              +
            </ZoomButton>
            <span className="px-1 py-1 text-center font-mono text-[8px] tabular-nums"
                  style={{ color: 'var(--graph-accent)', background: 'var(--graph-stage)' }}>
              {zoomLabel}
            </span>
            <ZoomButton onClick={() => zoomAtCentre(viewportRef.current, stageRef.current, 1 / 1.45)}>
              −
            </ZoomButton>
            <ZoomButton onClick={() => {
              const rect = stageRef.current?.getBoundingClientRect()
              reset(viewportRef.current, rect?.width ?? 800, rect?.height ?? 600)
            }} small>FIT</ZoomButton>
          </div>

          <div className="pointer-events-none absolute bottom-4 left-5 flex flex-col gap-1.5">
            {/* The legend follows the mode. A legend that describes an encoding the map is
                not currently using is worse than none — it is a caption for another chart. */}
            {LEGENDS[encode].map(([ink, label]) => (
              <span key={label} className="flex items-center gap-2 font-mono text-[9px]
                                           tracking-[0.13em] uppercase"
                    style={{ color: 'var(--graph-ink-faint)' }}>
                <i className="block size-[7px] rounded-full" style={{ background: ink }} />
                {label}
              </span>
            ))}
          </div>

          {hoveredNode ? (
            <div
              className="pointer-events-none absolute z-20 min-w-[180px] max-w-[280px] border
                         px-3 py-2 backdrop-blur-sm"
              style={{
                left: Math.min(Math.max(hovered!.sx, 100), 900),
                top: hovered!.sy,
                transform: 'translate(-50%, -125%)',
                borderColor: 'var(--graph-panel-line)',
                background: 'color-mix(in oklab, var(--graph-stage) 92%, transparent)',
              }}
            >
              <b className="mb-1.5 block text-[11.5px] leading-snug font-medium"
                 style={{ color: 'var(--graph-ink)' }}>{hoveredNode.title}</b>
              {hoveredNode.rows.map(([k, v]) => (
                <span key={k} className="flex justify-between gap-4 font-mono text-[9.5px]"
                      style={{ color: 'var(--graph-ink-faint)' }}>
                  {k}<i className="not-italic" style={{ color: 'var(--graph-ink-dim)' }}>{v}</i>
                </span>
              ))}
            </div>
          ) : null}
        </div>

        {/* ── right rail ── */}
        <aside className="axis-console-rail min-h-0 overflow-y-auto">
          {picked ? (
            <Detail picked={picked} graph={graph} onOpen={(id) =>
              navigate(`/war-room/threads/${id}`)} onFly={flyTo} />
          ) : (
            <Block title="Council alerts"
                   note={graph.alerts.length ? `${graph.alerts.length}` : 'clear'}
                   maxBodyHeight={0}>
              <p className="mb-3 text-[10.5px] leading-relaxed"
                 style={{ color: 'var(--graph-ink-faint)' }}>
                Derived from the council's assessments on every refresh, never stored — an alert
                disappears the moment the condition it names stops being true.
              </p>
              <AlertFeed alerts={graph.alerts} onPick={onAlert} />
            </Block>
          )}
        </aside>
      </div>

      {/* ── foot ── */}
      <footer className="flex shrink-0 items-center gap-5 border-t px-4 py-2 font-mono
                         text-[9px] tracking-[0.14em] uppercase"
              style={{ borderColor: 'var(--graph-panel-line)',
                       color: 'var(--graph-ink-faint)' }}>
        <span className="flex items-center gap-1.5">
          <i className={cn('block size-1.5 rounded-full', !paused && 'animate-pulse')}
             style={{ background: paused ? 'var(--graph-ink-faint)' : 'var(--graph-supportive)' }} />
          {paused ? 'Paused' : 'Live'} · {fmtDateTimeSec(graph.generatedAt)}
        </span>
        <span>Window {windowLabel(graph.windowHours)}</span>
        <span>Answer at {graph.deployThreshold}+ of 10</span>
        <span>{fmtNumber(graph.totals.engagements)} engagements</span>
        <span>{graph.totals.deployedToday} deployed in window</span>
        {/* A sample is never labelled as a population. */}
        {graph.truncated ? (
          <span style={{ color: 'var(--severity-medium)' }}>
            Drawing the {fmtNumber(graph.threads.length)} fastest of{' '}
            {fmtNumber(graph.threadTotal)} — counts above are over all of them
          </span>
        ) : (
          <span>Drawing all {fmtNumber(graph.threadTotal)} threads in the window</span>
        )}
        {error ? <span style={{ color: 'var(--severity-high)' }}>Refresh failed · {error}</span> : null}
        <span className="ml-auto">{immersive ? 'Esc to exit' : 'F for full screen'}</span>
      </footer>
    </div>
  )

  // In immersive mode the board is portalled to the body so it sits above the app shell rather
  // than inside it. Rendering it in place with a high z-index would still be clipped by the
  // shell's own `overflow: hidden`, which is what makes a takeover look half-applied.
  return immersive
    ? createPortal(
      <div className="fixed inset-0 z-[200]" role="dialog" aria-label="Narrative console, full screen">
        {board}
      </div>,
      document.body,
    )
    : board
}

/**
 * What the key says, per mode.
 *
 * REACH has no colour key because it has no colour encoding — size is the whole message, and
 * inventing swatches for it would imply a categorical reading that is not there.
 */
const LEGENDS: Record<EncodeMode, [string, string][]> = {
  THREAT: [
    ['var(--severity-critical)', 'Critical'],
    ['var(--severity-high)', 'Needs a response'],
    ['var(--severity-medium)', 'Approaching'],
    ['var(--severity-low)', 'Low'],
    ['var(--graph-unread)', 'Nobody has read this yet'],
    ['var(--graph-coordinated)', 'Coordinated narrative'],
  ],
  COORDINATION: [
    ['var(--graph-coordinated)', 'Matched repeated phrasing'],
    ['var(--graph-unread)', 'No coordination matched'],
    ['var(--graph-channel)', 'Subject, all narratives'],
  ],
  REACH: [
    ['var(--graph-accent)', 'Area \u221d engagements'],
    ['var(--graph-unread)', 'Engagement not published'],
  ],
}

/** What each encode mode is answering — the tooltip, so the mode row is self-explaining. */
const ENCODE_HINT: Record<EncodeMode, string> = {
  THREAT: 'Colour by the council\u2019s threat score. Grey means nobody has read it.',
  COORDINATION: 'Colour by whether the astroturf scan matched repeated phrasing. '
    + 'A pattern in the text, never a claim about a person.',
  REACH: 'Size by engagements, one accent, no hue. A magnitude is a length, never a colour.',
}

export interface LogLine {
  id: string
  at: string
  text: string
  severity?: string
}

/**
 * The live activity feed.
 *
 * Newest first, and it says "no change" out loud on a quiet refresh. A log that only prints
 * when something happens cannot be told apart from a log that has stopped working, and this
 * board is watched precisely at the times when that distinction matters.
 */
function SignalLog({ lines }: { lines: LogLine[] }) {
  if (!lines.length) {
    return <Empty>Waiting for the first refresh.</Empty>
  }
  return (
    <div className="flex flex-col">
      {lines.slice(0, 24).map((line) => (
        <div key={line.id}
             className="axis-console-in grid grid-cols-[auto_1fr] gap-2 border-b
                        border-white/[0.03] py-1.5 text-[11px] last:border-b-0">
          <time className="pt-px font-mono text-[9.5px] tabular-nums"
                style={{ color: 'var(--graph-ink-faint)' }}>{line.at}</time>
          <p className="leading-snug"
             style={{
               color: line.severity === 'CRITICAL' || line.severity === 'HIGH'
                 ? 'var(--severity-high)'
                 : 'var(--graph-ink-dim)',
             }}>{line.text}</p>
        </div>
      ))}
    </div>
  )
}

/**
 * The console's seal.
 *
 * Drawn rather than imported so it inherits the accent token and needs no asset. It is the one
 * piece of pure insignia on the screen, and it is here because this board gets projected in
 * rooms where an unlabelled black rectangle reads as somebody's side project.
 */
function Seal() {
  return (
    <svg viewBox="0 0 40 40" className="size-8 shrink-0" aria-hidden focusable="false"
         style={{ color: 'var(--graph-accent)' }}>
      <circle cx="20" cy="20" r="18.5" fill="none" stroke="currentColor" strokeOpacity=".45" />
      <circle cx="20" cy="20" r="14" fill="none" stroke="currentColor" strokeOpacity=".22" />
      <circle cx="20" cy="20" r="3.2" fill="currentColor" fillOpacity=".8" />
      {Array.from({ length: 12 }, (_, i) => {
        const a = (i / 12) * Math.PI * 2 - Math.PI / 2
        const long = i % 3 === 0
        return (
          <line
            key={i}
            x1={20 + Math.cos(a) * (long ? 14 : 16)}
            y1={20 + Math.sin(a) * (long ? 14 : 16)}
            x2={20 + Math.cos(a) * 18.5}
            y2={20 + Math.sin(a) * 18.5}
            stroke="currentColor"
            strokeOpacity={long ? '.7' : '.3'}
          />
        )
      })}
      <path d="M20 6.5 L20 33.5 M6.5 20 L33.5 20" stroke="currentColor" strokeOpacity=".14" />
    </svg>
  )
}

function ExpandIcon({ collapsed }: { collapsed: boolean }) {
  return (
    <svg viewBox="0 0 16 16" className="size-4" aria-hidden focusable="false"
         fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="square">
      {collapsed ? (
        <>
          <path d="M6.5 2v4.5H2M9.5 2v4.5H14M6.5 14V9.5H2M9.5 14V9.5H14" />
        </>
      ) : (
        <>
          <path d="M2 6V2h4M14 6V2h-4M2 10v4h4M14 10v4h-4" />
        </>
      )}
    </svg>
  )
}

// ── the right rail's detail panes ────────────────────────────────────────────

function Detail({ picked, graph, onOpen, onFly }: {
  picked: Picked
  graph: NarrativeGraph
  onOpen: (threadId: string) => void
  onFly: (sel: Selection, k?: number) => void
}) {
  if (picked.kind === 'subject') {
    return <SubjectDetail node={picked.node} graph={graph} onFly={onFly} />
  }
  if (picked.kind === 'narrative') {
    return <NarrativeDetail node={picked.node} graph={graph} onOpen={onOpen} onFly={onFly} />
  }
  return <ThreadDetail node={picked.node} graph={graph} onOpen={onOpen} onFly={onFly} />
}

function SubjectDetail({ node, graph, onFly }: {
  node: SubjectNode
  graph: NarrativeGraph
  onFly: (sel: Selection, k?: number) => void
}) {
  const own = graph.narratives.filter((n) => n.subjectId === node.id)
  return (
    <>
      <Block title="Subject">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Pill ink={threatInk(node.threatMax, graph.deployThreshold)}>
            {node.threatMax === null ? 'Not scored' : `Peak ${threatText(node.threatMax)}`}
          </Pill>
          <Pill>{node.threads} threads</Pill>
        </div>
        <h3 className="mb-3 text-[19px] leading-snug font-normal"
            style={{ color: 'var(--graph-ink)' }}>{node.name ?? 'Unnamed subject'}</h3>
        <Grid>
          <Cell label="Narratives" value={node.narratives} />
          <Cell label="Velocity" value={node.velocity.toFixed(1)} />
          <Cell label="Engagements" value={fmtNumber(node.engagements)} />
          <Cell label="Hostile" value={`${Math.round(node.hostileShare * 100)}%`}
                ink={node.hostileShare > 0.5 ? 'var(--severity-high)' : undefined} />
        </Grid>
        <p className="text-[10.5px] leading-relaxed" style={{ color: 'var(--graph-ink-faint)' }}>
          Hostile share is of the threads the council has actually read. It is not a share of
          everything said about this subject, and it moves as more threads are assessed.
        </p>
      </Block>
      <Block title="Its narratives" note={`${own.length}`}>
        <NarrativeList narratives={own} selectedId={null}
                       onPick={(id) => onFly({ kind: 'narrative', id })}
                       threshold={graph.deployThreshold} />
      </Block>
    </>
  )
}

function NarrativeDetail({ node, graph, onOpen, onFly }: {
  node: NarrativeNode
  graph: NarrativeGraph
  onOpen: (threadId: string) => void
  onFly: (sel: Selection, k?: number) => void
}) {
  const coordinated = node.kind === 'COORDINATED'
  const own = graph.threads.filter((t) => t.narrativeId === node.id)
  return (
    <>
      <Block title="Narrative">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Pill ink={coordinated
            ? 'var(--graph-coordinated)'
            : threatInk(node.threatMax, graph.deployThreshold)}>
            {coordinated ? 'Coordinated' : 'Organic'}
          </Pill>
          <Pill>{node.threads} threads</Pill>
        </div>
        <h3 className="mb-3 text-[17px] leading-snug font-normal"
            style={{ color: 'var(--graph-ink)' }}>{node.label}</h3>

        {coordinated ? (
          <p className="mb-4 border-l pl-3 text-[11.5px] leading-relaxed"
             style={{ color: 'var(--graph-ink-dim)', borderColor: 'var(--graph-panel-line)' }}>
            The same wording appears in {node.threads} threads from {node.distinctAuthors}{' '}
            distinct authors, at {node.coordinationConfidence}% confidence. This is a pattern in
            the text. It is not a finding about any person behind it, and it is not evidence
            that the claim is false.
          </p>
        ) : (
          <p className="mb-4 border-l pl-3 text-[11.5px] leading-relaxed"
             style={{ color: 'var(--graph-ink-dim)', borderColor: 'var(--graph-panel-line)' }}>
            Threads the astroturf scan did not match, grouped by the sentiment the council read.
            A grouping, not a finding — these are people who happen to be saying similar things.
          </p>
        )}

        <Grid>
          <Cell label="Peak threat"
                value={threatText(node.threatMax)}
                ink={threatInk(node.threatMax, graph.deployThreshold)} />
          <Cell label="Mean threat" value={threatText(node.threatMean)} />
          <Cell label="Velocity" value={node.velocity.toFixed(1)} />
          <Cell label="Engagements" value={fmtNumber(node.engagements)} />
        </Grid>

        <div className="mb-4 space-y-1.5">
          <div className="axis-console-eyebrow">Channels</div>
          <div className="flex flex-wrap gap-1.5">
            {node.platforms.map((code) => <Pill key={code}>{code}</Pill>)}
          </div>
        </div>

        <div className="space-y-1.5">
          <div className="axis-console-eyebrow">Seen</div>
          <p className="text-[11px]" style={{ color: 'var(--graph-ink-dim)' }}>
            First {fmtRelative(node.firstSeenAt)} · last {fmtRelative(node.lastSeenAt)}
          </p>
        </div>
      </Block>

      <Block title="Threads" note={`${own.length}`}>
        <ThreadList threads={own} onOpen={onOpen} onFly={onFly} threshold={graph.deployThreshold} />
      </Block>
    </>
  )
}

function ThreadDetail({ node, graph, onOpen, onFly }: {
  node: ThreadNode
  graph: NarrativeGraph
  onOpen: (threadId: string) => void
  onFly: (sel: Selection, k?: number) => void
}) {
  const narrative = graph.narratives.find((n) => n.id === node.narrativeId)
  const above = node.threat !== null && node.threat >= graph.deployThreshold
  return (
    <Block title="Thread">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Pill ink={threatInk(node.threat, graph.deployThreshold)}>
          {node.threat === null ? 'Not read' : `Threat ${threatText(node.threat)}`}
        </Pill>
        <Pill>{node.platformCode}</Pill>
        {node.deployedCount > 0 ? (
          <Pill ink="var(--graph-supportive)">Answered</Pill>
        ) : null}
      </div>

      <h3 className="mb-2 text-[15px] leading-snug font-normal"
          style={{ color: 'var(--graph-ink)' }}>
        {node.title ?? 'Untitled thread'}
      </h3>
      <p className="mb-4 font-mono text-[10px] tracking-[0.1em]"
         style={{ color: 'var(--graph-ink-faint)' }}>
        {node.authorHandle ?? 'unknown author'} · {node.subjectName ?? 'unscoped'} ·{' '}
        {fmtRelative(node.firstSeenAt)}
      </p>

      {node.threat === null ? (
        <p className="mb-4 border-l pl-3 text-[11.5px] leading-relaxed"
           style={{ color: 'var(--graph-ink-dim)', borderColor: 'var(--severity-medium)' }}>
          Nobody has assessed this thread. That is not the same as a low score — it means the
          council has never read it, and nothing on this board is a judgement about it yet.
        </p>
      ) : null}

      <Grid>
        <Cell label="Velocity" value={node.velocity === null ? '—' : node.velocity.toFixed(1)} />
        <Cell label="Engagements"
              value={node.engagements === null ? 'Unknown' : fmtNumber(node.engagements)} />
        <Cell label="Sentiment" value={node.sentiment ?? '—'} />
        <Cell label="Framing" value={node.framing ?? '—'} />
      </Grid>

      <div className="mb-4 space-y-1.5">
        <div className="axis-console-eyebrow">Status</div>
        <p className="text-[11px]" style={{ color: 'var(--graph-ink-dim)' }}>
          {node.status} · {node.draftCount} draft{node.draftCount === 1 ? '' : 's'} ·{' '}
          {node.deployedCount} deployed
        </p>
        {above && node.deployedCount === 0 && node.draftCount === 0 ? (
          <p className="text-[11px]" style={{ color: 'var(--severity-high)' }}>
            Serious enough to answer, and nothing written yet.
          </p>
        ) : null}
      </div>

      <div className="flex flex-col gap-1.5">
        <button
          type="button"
          onClick={() => onOpen(node.id)}
          className="border px-3 py-2 text-[11px] transition-colors hover:opacity-85"
          style={{ borderColor: 'var(--graph-accent)', color: 'var(--graph-accent)' }}
        >
          Open the thread →
        </button>
        {narrative ? (
          <button
            type="button"
            onClick={() => onFly({ kind: 'narrative', id: narrative.id })}
            className="border px-3 py-2 text-[11px] transition-colors hover:opacity-85"
            style={{ borderColor: 'var(--graph-panel-line)', color: 'var(--graph-ink-dim)' }}
          >
            Show its narrative
          </button>
        ) : null}
        {node.url ? (
          <a
            href={node.url}
            target="_blank"
            rel="noreferrer noopener"
            className="border px-3 py-2 text-center text-[11px] transition-colors hover:opacity-85"
            style={{ borderColor: 'var(--graph-panel-line)', color: 'var(--graph-ink-dim)' }}
          >
            View on {node.platformCode} ↗
          </a>
        ) : null}
      </div>
    </Block>
  )
}

function ThreadList({ threads, onOpen, onFly, threshold }: {
  threads: ThreadNode[]
  onOpen: (id: string) => void
  onFly: (sel: Selection, k?: number) => void
  threshold: number
}) {
  if (!threads.length) return <Empty>No thread here on the visible channels.</Empty>
  return (
    <div className="space-y-0.5">
      {threads.slice(0, 30).map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => onFly({ kind: 'thread', id: t.id })}
          onDoubleClick={() => onOpen(t.id)}
          className="flex w-full items-start gap-2 rounded-sm px-1.5 py-1.5 text-left
                     transition-colors hover:bg-white/[0.04]"
        >
          <span className="mt-1 size-1.5 shrink-0 rounded-full"
                style={{ background: threatInk(t.threat, threshold) }} />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[11px]" style={{ color: 'var(--graph-ink)' }}>
              {t.title ?? t.authorHandle ?? 'Untitled'}
            </span>
            <span className="block font-mono text-[8.5px] tracking-[0.1em]"
                  style={{ color: 'var(--graph-ink-faint)' }}>
              {t.platformCode} ·{' '}
              {t.threat === null
                ? 'NOT READ'
                : `THREAT ${threatText(t.threat)}${t.threat >= threshold ? ' · OVER' : ''}`}
            </span>
          </span>
        </button>
      ))}
      {threads.length > 30 ? (
        <p className="px-1.5 pt-2 font-mono text-[9px] tracking-[0.12em] uppercase"
           style={{ color: 'var(--graph-ink-faint)' }}>
          + {threads.length - 30} more in this narrative
        </p>
      ) : null}
    </div>
  )
}

// ── small helpers ────────────────────────────────────────────────────────────

function ZoomButton({ children, onClick, small }: {
  children: React.ReactNode
  onClick: () => void
  small?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn('axis-keep-narrow h-7 w-8 leading-none transition-colors hover:opacity-80',
        small ? 'text-[9px]' : 'text-[12px]')}
      style={{ background: 'var(--graph-stage)', color: 'var(--graph-ink-faint)' }}
    >
      {children}
    </button>
  )
}

function zoomAtCentre(v: Viewport, stage: HTMLDivElement | null, factor: number) {
  const rect = stage?.getBoundingClientRect()
  zoomAt(v, (rect?.width ?? 800) / 2, (rect?.height ?? 600) / 2, factor)
}

/** What the tooltip says, per level. Kept out of the render loop — it is DOM, not canvas. */
function describeHover(
  graph: NarrativeGraph, hover: Hover,
): { title: string, rows: [string, string][] } | null {
  if (hover.kind === 'platform') {
    const p = graph.platforms.find((x) => x.code === hover.id)
    if (!p) return null
    return {
      title: p.label,
      rows: [
        ['Threads', fmtNumber(p.threads)],
        ['Share', `${Math.round(p.share * 100)}%`],
        ['Over threshold', fmtNumber(p.aboveThreshold)],
        ['Reply API', p.canPost ? 'Yes' : 'Watch only'],
      ],
    }
  }
  if (hover.kind === 'subject') {
    const s = graph.subjects.find((x) => x.id === hover.id)
    if (!s) return null
    return {
      title: s.name ?? 'Unnamed subject',
      rows: [
        ['Threads', fmtNumber(s.threads)],
        ['Narratives', String(s.narratives)],
        ['Peak threat', s.threatMax === null ? 'Not scored' : `${s.threatMax}/10`],
        ['Hostile', `${Math.round(s.hostileShare * 100)}%`],
      ],
    }
  }
  if (hover.kind === 'narrative') {
    const n = graph.narratives.find((x) => x.id === hover.id)
    if (!n) return null
    return {
      title: n.label,
      rows: [
        ['Kind', n.kind === 'COORDINATED' ? 'Coordinated' : 'Organic'],
        ['Threads', fmtNumber(n.threads)],
        ['Peak threat', n.threatMax === null ? 'Not scored' : `${n.threatMax}/10`],
        ...(n.kind === 'COORDINATED'
          ? ([['Authors', String(n.distinctAuthors)]] as [string, string][])
          : ([['Sentiment', n.sentiment]] as [string, string][])),
      ],
    }
  }
  const t = graph.threads.find((x) => x.id === hover.id)
  if (!t) return null
  return {
    title: t.title ?? t.authorHandle ?? 'Untitled thread',
    rows: [
      ['Platform', t.platformCode],
      ['Threat', t.threat === null ? 'Not read' : `${t.threat}/10`],
      ['Velocity', t.velocity === null ? '—' : t.velocity.toFixed(1)],
      ['Engagements', t.engagements === null ? 'Unknown' : fmtNumber(t.engagements)],
    ],
  }
}
